<template>
  <b-modal size="lg" v-model="showForm" :title="$t('modalTitle')" no-close-on-backdrop no-enforce-focus>
    <!-- Trạng thái KYC (info only) -->
    <div v-if="formModel.confirmTime" class="alert alert-success py-2 mb-3">
      <i class="fa fa-check-circle mr-1"></i>
      <strong>{{ $t('kycConfirmed') }}</strong>
      — {{ formModel.confirmTime | moment('DD/MM/YY HH:mm') }}
    </div>
    <div v-else-if="formModel.cancelTime" class="alert alert-danger py-2 mb-3">
      <i class="fa fa-times-circle mr-1"></i>
      <strong>{{ $t('kycRejected') }}</strong>
      — {{ formModel.cancelTime | moment('DD/MM/YY HH:mm') }}
    </div>
    <div v-else class="alert alert-warning py-2 mb-3">
      <i class="fa fa-clock mr-1"></i>
      <strong>{{ $t('kycWaiting') }}</strong>
    </div>

    <!-- Thông tin chung -->
    <fieldset class="kyc-section mb-3">
      <legend class="kyc-legend"><i class="fa fa-info-circle mr-1"></i>{{ $t('clientInfo') }}</legend>
      <dl class="row mb-0">
        <dt class="col-sm-4 text-muted">{{ $t('client') }}</dt>
        <dd class="col-sm-8">
          <b v-if="formModel.client">{{ formModel.client.companyName }}</b>
          <code v-if="formModel.client" class="ml-2">{{ formModel.client.domain }}</code>
        </dd>
        <dt class="col-sm-4 text-muted">{{ $t('customer') }}</dt>
        <dd class="col-sm-8">
          <span :class="formModel.type === 'enterprise' ? 'badge badge-primary' : 'badge badge-secondary'">
            {{ $t(formModel.type) }}
          </span>
        </dd>
      </dl>
    </fieldset>

    <!-- Hợp đồng đã upload trước (nếu có) -->
    <fieldset v-if="formModel.contract && formModel.contractUrl" class="kyc-section mb-3">
      <legend class="kyc-legend"><i class="fa fa-file-signature mr-1"></i>{{ $t('currentContract') }}</legend>
      <a :href="formModel.contractUrl" target="_blank" class="btn btn-outline-success is-border-1">
        <i class="fa fa-download mr-1"></i>{{ $t('downloadCurrent') }}
      </a>
      <small class="text-muted ml-2" v-if="formModel.sendContractTime">
        ({{ $t('uploadedAt') }} {{ formModel.sendContractTime | moment('DD/MM/YY HH:mm') }})
      </small>
    </fieldset>

    <!-- MISA WeSign — paste documentId từ dashboard MISA -->
    <fieldset class="kyc-section mb-3">
      <legend class="kyc-legend"><i class="fa fa-signature mr-1"></i>MISA WeSign</legend>

      <!-- Đã liên kết -->
      <div v-if="formModel.wesignAmisappRef" class="small">
        <div class="d-flex align-items-center mb-2">
          <span class="badge mr-2" :class="wesignBadgeClass">{{ wesignStatusLabel }}</span>
          <strong v-if="formModel.wesignDocumentName" class="mr-2">{{ formModel.wesignDocumentName }}</strong>
          <code class="text-monospace small">{{ formModel.wesignAmisappRef }}</code>
        </div>
        <div class="text-muted mb-2">
          <span v-if="formModel.wesignLinkTime">
            Liên kết: {{ formModel.wesignLinkTime | moment('DD/MM/YY HH:mm') }}
          </span>
          <span v-if="formModel.wesignCompleteTime" class="ml-2 text-success">
            • Hoàn thành: {{ formModel.wesignCompleteTime | moment('DD/MM/YY HH:mm') }}
          </span>
        </div>
        <div class="mb-2">
          <a :href="'https://amisapp.misa.vn/wesign/documents/waiting-for-others/' + formModel.wesignAmisappRef"
             target="_blank" class="btn btn-sm btn-outline-info is-border-1 mr-1">
            <i class="fa fa-external-link-alt mr-1"></i>Mở trên MISA
          </a>
          <button class="btn btn-sm btn-outline-primary is-border-1 mr-1" @click="onSyncWesign" :disabled="wesignBusy">
            <i class="fa fa-sync mr-1" :class="{'fa-spin': wesignBusy}"></i>Đồng bộ ngay
          </button>
          <button class="btn btn-sm btn-outline-danger is-border-1" @click="onUnlinkWesign" :disabled="wesignBusy">
            <i class="fa fa-unlink mr-1"></i>Bỏ liên kết
          </button>
        </div>
        <div v-if="formModel.wesignParticipants && formModel.wesignParticipants.length" class="mb-2">
          <small class="text-muted">
            Khách đã ký: <strong :class="customerAllSigned ? 'text-success' : 'text-warning'">
              {{ customerSignedCount }}/{{ customerTotalCount }}
            </strong>
            <span class="ml-2 font-italic">(chỉ tính người ngoài Phonenet, sếp nội bộ ký nhưng không tính vào tiến độ khách)</span>
          </small>
        </div>
        <table v-if="formModel.wesignParticipants && formModel.wesignParticipants.length"
               class="table table-sm table-bordered mb-2 bg-white">
          <thead class="thead-light"><tr><th>#</th><th>Người ký</th><th>Vai trò</th><th>Email</th><th>Trạng thái</th></tr></thead>
          <tbody>
            <tr v-for="(p, idx) in formModel.wesignParticipants" :key="idx"
                :class="{'table-light': isInternalParticipant(p)}">
              <td class="align-middle">{{ p.priority || idx + 1 }}</td>
              <td>{{ p.name }}</td>
              <td>
                <span v-if="isInternalParticipant(p)" class="badge badge-secondary">Nội bộ</span>
                <span v-else class="badge badge-info">Khách</span>
              </td>
              <td class="small">{{ p.email }}</td>
              <td>
                <span class="badge" :class="participantBadgeClass(p.action)">
                  {{ participantActionLabel(p.action) }}
                </span>
                <span v-if="p.signTime" class="text-muted small ml-1">
                  ({{ p.signTime | moment('DD/MM HH:mm') }})
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <details v-if="formModel.wesignNotifySchedule && formModel.wesignNotifySchedule.length" class="small text-muted">
          <summary>Lịch sử thông báo ({{ formModel.wesignNotifySchedule.length }} bước)</summary>
          <ul class="mt-1 mb-0 pl-3">
            <li v-for="(s, idx) in formModel.wesignNotifySchedule" :key="idx">
              <strong>{{ s.step }}</strong> — {{ s.triggeredTime | moment('DD/MM HH:mm') }}
              ({{ (s.channels || []).join(', ') }})
              <span v-if="!s.success" class="text-danger">✗ {{ s.errorMsg }}</span>
            </li>
          </ul>
        </details>
      </div>

      <!-- Chưa liên kết -->
      <div v-else>
        <div class="form-group mb-2">
          <label class="font-weight-bold mb-1">Mã hợp đồng MISA</label>
          <small class="form-text text-muted mb-2">
            Paste UUID hoặc URL từ dashboard MISA (vd
            <code>https://amisapp.misa.vn/wesign/documents/.../ce886d2d-6b0c-46fa-8387-722af67b800c</code>).
            Worker sẽ tự đồng bộ trạng thái mỗi 30 phút.
          </small>
          <div class="input-group">
            <input class="form-control" v-model="wesignRefInput"
                   placeholder="Paste UUID hoặc URL MISA tại đây"
                   :disabled="wesignBusy"/>
            <div class="input-group-append">
              <button class="btn btn-outline-primary" @click="onLinkWesign"
                      :disabled="wesignBusy || !wesignRefInput || !wesignRefInput.trim()">
                <i class="fa fa-link mr-1"></i>Liên kết
              </button>
            </div>
          </div>
        </div>
      </div>
    </fieldset>

    <!-- Form upload + note -->
    <fieldset class="kyc-section">
      <legend class="kyc-legend">
        <i class="fa fa-upload mr-1"></i>{{ formModel.contract ? $t('replaceContract') : $t('uploadContract') }}
      </legend>
      <div class="form-group">
        <label class="font-weight-bold mb-1">{{ $t('contractFile') }} <span class="text-danger">*</span></label>
        <small class="form-text text-muted mb-2">{{ $t('contractFileHint') }}</small>
        <form-file service="/upload/upload-kyc" :model="formModel" attribute="contract" :errors="formErrors"
                   :accept="'image/jpeg,image/jpg,application/pdf'">
        </form-file>
      </div>
      <div class="form-group mb-0">
        <label class="font-weight-bold mb-1">{{ $t('note') }}</label>
        <small class="form-text text-muted mb-2">{{ $t('noteHint') }}</small>
        <form-textarea :rows="3" attribute="note" :model="formModel" :errors="formErrors" :placeholder="$t('notePlaceholder')" />
      </div>
    </fieldset>

    <template v-slot:modal-footer>
      <div class="d-flex w-100 justify-content-between align-items-center">
        <button class="btn btn-secondary" @click="showForm = false">
          {{ $t('close') }}
        </button>
        <div>
          <button
            v-if="formModel._id && formModel.type && ['owner', 'admin', 'tech', 'operationsManager', 'account', 'accountant', 'sales'].includes($store.state.user.role)"
            class="btn btn-success mr-2"
            @click="$refs.exportContract.open(formModel._id)">
            <i class="fa fa-file-pdf mr-1"></i>{{ $t('exportContract') }}
          </button>
          <button v-if="!['accountant'].includes($store.state.user.role)"
                  class="btn btn-primary" @click="onSave"
                  :disabled="!formModel.contract || processing">
            <i class="fa fa-save mr-1"></i>{{ $t('saveBtn') }}
          </button>
        </div>
      </div>
    </template>
    <export-contract-modal ref="exportContract"></export-contract-modal>
  </b-modal>
</template>

<script>
import CRUD from "../../../mixins/CRUD";
import ExportContractModal from "./ExportContractModal";

export default {
  mixins: [CRUD],
  components: {
    ExportContractModal,
  },
  data: function () {
    return {
      service: '/client-kyc',
      reloadBus: 'reloadKYC',
      showForm: false,
      processing: false,
      wesignRefInput: '',
      wesignBusy: false,
    }
  },
  computed: {
    wesignStatusLabel() {
      const m = {
        pending: 'Chờ ký',
        inProgress: 'Đang ký',
        completed: '✓ Đã ký xong',
        rejected: '✗ Khách từ chối',
        cancelled: 'Đã huỷ',
        expired: 'Hết hạn',
      };
      return m[this.formModel.wesignStatus] || 'Chưa rõ';
    },
    customerParticipants() {
      return (this.formModel.wesignParticipants || []).filter(p => !this.isInternalParticipant(p));
    },
    customerSignedCount() {
      return this.customerParticipants.filter(p => p.action === 2).length;
    },
    customerTotalCount() { return this.customerParticipants.length; },
    customerAllSigned() {
      return this.customerTotalCount > 0 && this.customerSignedCount === this.customerTotalCount;
    },
    wesignBadgeClass() {
      const m = {
        pending: 'badge-secondary',
        inProgress: 'badge-info',
        completed: 'badge-success',
        rejected: 'badge-danger',
        cancelled: 'badge-warning',
        expired: 'badge-dark',
      };
      return m[this.formModel.wesignStatus] || 'badge-light';
    },
  },
  methods: {
    isInternalParticipant(p) {
      // Nội bộ Phonenet/Callio (vd Giang Thiên Phú giám đốc) — không tính vào tiến độ khách
      return !!(p && p.email && p.email.toLowerCase().includes('@gadget.com.vn'));
    },
    participantActionLabel(action) {
      const m = {0: 'Chưa', 1: 'Đang chờ ký', 2: 'Đã ký', 3: 'Từ chối', 4: 'Chờ điều phối', 5: 'Đã điều phối', 6: 'Chờ phê duyệt', 7: 'Đã phê duyệt'};
      return m[action] || '?';
    },
    participantBadgeClass(action) {
      const m = {2: 'badge-success', 3: 'badge-danger', 1: 'badge-info'};
      return m[action] || 'badge-light';
    },
    async onLinkWesign() {
      const raw = (this.wesignRefInput || '').trim();
      if (!raw) return;
      this.wesignBusy = true;
      try {
        const res = await this.$service.post(`/client-kyc/${this.formModel._id}/link-wesign-ref`, {wesignAmisappRef: raw});
        if (res.data && res.data.success) {
          this.$set(this.formModel, 'wesignAmisappRef', res.data.wesignAmisappRef);
          this.$set(this.formModel, 'wesignStatus', res.data.wesignStatus);
          this.$set(this.formModel, 'wesignDocumentName', res.data.wesignDocumentName);
          this.$set(this.formModel, 'wesignParticipants', res.data.wesignParticipants || []);
          this.$set(this.formModel, 'wesignLinkTime', Date.now());
          this.wesignRefInput = '';
          this.$swal({title: 'Đã liên kết MISA WeSign', icon: 'success', toast: true, position: 'top-end', timer: 3000, showConfirmButton: false});
          if (this.reloadBus) this.$bus.$emit(this.reloadBus);
        }
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.message;
        this.$swal({text: msg || 'Liên kết MISA thất bại', icon: 'error'});
      } finally {
        this.wesignBusy = false;
      }
    },
    async onSyncWesign() {
      this.wesignBusy = true;
      try {
        const res = await this.$service.post(`/client-kyc/${this.formModel._id}/sync-wesign-ref`);
        if (res.data && res.data.success) {
          this.$set(this.formModel, 'wesignStatus', res.data.wesignStatus);
          this.$set(this.formModel, 'wesignDocumentName', res.data.wesignDocumentName);
          this.$set(this.formModel, 'wesignParticipants', res.data.wesignParticipants || []);
          this.$swal({title: 'Đã đồng bộ', icon: 'success', toast: true, position: 'top-end', timer: 2000, showConfirmButton: false});
        }
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.message;
        this.$swal({text: msg || 'Đồng bộ thất bại', icon: 'error'});
      } finally {
        this.wesignBusy = false;
      }
    },
    async onUnlinkWesign() {
      const ok = await this.$swal({
        title: 'Bỏ liên kết MISA WeSign?',
        text: 'Sẽ xoá mã hợp đồng + lịch sử thông báo. Không ảnh hưởng dữ liệu phía MISA.',
        icon: 'warning', showCancelButton: true,
      });
      if (!ok.value) return;
      this.wesignBusy = true;
      try {
        await this.$service.delete(`/client-kyc/${this.formModel._id}/wesign-ref`);
        this.$set(this.formModel, 'wesignAmisappRef', null);
        this.$set(this.formModel, 'wesignStatus', null);
        this.$set(this.formModel, 'wesignDocumentName', null);
        this.$set(this.formModel, 'wesignParticipants', []);
        this.$set(this.formModel, 'wesignNotifySchedule', []);
        this.$set(this.formModel, 'wesignLinkTime', null);
        this.$set(this.formModel, 'wesignCompleteTime', null);
        this.$swal({title: 'Đã bỏ liên kết', icon: 'success', toast: true, position: 'top-end', timer: 2000, showConfirmButton: false});
        if (this.reloadBus) this.$bus.$emit(this.reloadBus);
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.message;
        this.$swal({text: msg || 'Bỏ liên kết thất bại', icon: 'error'});
      } finally {
        this.wesignBusy = false;
      }
    },
    open(item) {
      this.formModel = item;
      this.wesignRefInput = '';
      this.showForm = true;
    },
    onSave() {
      if (!this.formModel.contract) {
        this.$swal({title: this.$t('contractRequired'), icon: 'warning'});
        return;
      }
      this.$swal({
        title: this.$t('saveConfirmTitle'),
        text: this.$t('saveConfirmText'),
        icon: 'question',
        showCancelButton: true,
      }).then(result => {
        if (!result.value) return;
        this.processing = true;
        const loader = this.$loading.show();
        const url = `${this.service}/send-contract/${this.formModel._id}`;
        this.$service.put(url, this.formModel).then(response => {
          if (response && response.data) {
            this.$swal({
              title: this.$t('success'),
              text: this.$t('saveSuccess'),
              icon: 'success',
              toast: true,
              timer: 4000,
              position: 'top-end',
            });
            this.showForm = false;
            if (this.reloadBus) this.$bus.$emit(this.reloadBus);
          }
        }).catch(error => {
          if (error.response && error.response.data && error.response.data.message) {
            this.$swal({text: error.response.data.message, icon: 'error'});
          } else {
            this.formErrors = (error.response && error.response.data) || [];
          }
        }).finally(() => {
          this.processing = false;
          loader.hide();
        });
      });
    },
  },
  i18n: {
    messages: {
      en: {
        modalTitle: 'Signed contract management',
        clientInfo: 'Client info',
        currentContract: 'Current uploaded contract',
        downloadCurrent: 'Download current file',
        uploadedAt: 'uploaded at',
        uploadContract: 'Upload signed contract',
        replaceContract: 'Replace signed contract',
        contractFile: 'Signed contract file',
        contractFileHint: 'Upload PDF or JPG of the contract signed by customer (max 10MB)',
        contractRequired: 'Please select a file before saving',
        note: 'Note',
        noteHint: 'Optional note about this contract',
        notePlaceholder: 'E.g. signed via post on 06/05/2026',
        kycConfirmed: 'KYC profile approved',
        kycRejected: 'KYC profile has been rejected',
        kycWaiting: 'KYC profile waiting for review',
        close: 'Close',
        saveBtn: 'Save',
        success: 'Success',
        saveConfirmTitle: 'Save signed contract?',
        saveConfirmText: 'The file will be saved to KYC. The customer will not be notified.',
        saveSuccess: 'Contract file saved',
        client: 'Client',
        customer: 'Type',
        personal: 'Personal',
        enterprise: 'Enterprise',
        exportContract: 'Export PDF',
      },
      vi: {
        modalTitle: 'Hợp đồng đã ký',
        clientInfo: 'Thông tin khách',
        currentContract: 'File hợp đồng đang lưu',
        downloadCurrent: 'Tải file hiện tại',
        uploadedAt: 'tải lên lúc',
        uploadContract: 'Tải lên hợp đồng đã ký',
        replaceContract: 'Thay thế hợp đồng đã ký',
        contractFile: 'File hợp đồng đã ký',
        contractFileHint: 'Upload file PDF/JPG do khách ký (tối đa 10MB)',
        contractRequired: 'Vui lòng chọn file trước khi lưu',
        note: 'Ghi chú',
        noteHint: 'Ghi chú thêm (tuỳ chọn)',
        notePlaceholder: 'VD: ký qua đường bưu điện ngày 06/05/2026',
        kycConfirmed: 'Hồ sơ KYC đã được duyệt',
        kycRejected: 'Hồ sơ KYC đã bị từ chối',
        kycWaiting: 'Hồ sơ KYC đang chờ duyệt',
        close: 'Đóng',
        saveBtn: 'Lưu',
        success: 'Thành công',
        saveConfirmTitle: 'Lưu file hợp đồng đã ký?',
        saveConfirmText: 'File sẽ được lưu vào KYC. Khách hàng KHÔNG được thông báo.',
        saveSuccess: 'Đã lưu file hợp đồng',
        client: 'Khách',
        customer: 'Loại',
        personal: 'Cá nhân',
        enterprise: 'Doanh nghiệp',
        exportContract: 'Xuất PDF',
      }
    }
  }
}
</script>

<style scoped>
.kyc-section {
  border: 1px solid #e3e6f0;
  border-radius: 6px;
  padding: 12px 16px;
  background: #fff;
}

.kyc-legend {
  width: auto;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #5a5c69;
  padding: 2px 8px;
  margin-bottom: 8px;
  background: #f8f9fc;
  border-radius: 4px;
  border: 1px solid #e3e6f0;
}

.kyc-section dl.row dt {
  font-weight: 500;
  margin-bottom: 6px;
}

.kyc-section dl.row dd {
  margin-bottom: 6px;
  word-break: break-word;
}
</style>
